import { z } from 'zod';

export const YSTNJ_COGNITO_GROUPS = [
  'staff',
  'parent',
  'nurse',
  'owner',
  'admin',
  'viewer',
  'editor',
  'super-administrator',
  'admissions-review',
  'financial-aid-review',
] as const;

export const YSTNJ_REVIEW_GROUPS = [
  'admissions-review',
  'financial-aid-review',
] as const;

export const YSTNJ_REVIEW_MODULE_IDS = [
  'admissions',
  'financial-aid',
] as const;

export const YSTNJ_RUNTIME_PERSONA_PROJECTIONS = [
  'parent',
  'staff',
  'administrator',
  'super-administrator',
  'viewer',
  'guest',
] as const;

export const YSTNJ_COGNITO_GROUP = {
  STAFF: YSTNJ_COGNITO_GROUPS[0],
  PARENT: YSTNJ_COGNITO_GROUPS[1],
  NURSE: YSTNJ_COGNITO_GROUPS[2],
  OWNER: YSTNJ_COGNITO_GROUPS[3],
  ADMIN: YSTNJ_COGNITO_GROUPS[4],
  VIEWER: YSTNJ_COGNITO_GROUPS[5],
  EDITOR: YSTNJ_COGNITO_GROUPS[6],
  SUPER_ADMINISTRATOR: YSTNJ_COGNITO_GROUPS[7],
  ADMISSIONS_REVIEW: YSTNJ_COGNITO_GROUPS[8],
  FINANCIAL_AID_REVIEW: YSTNJ_COGNITO_GROUPS[9],
} as const;

export const YSTNJ_REVIEW_GROUP = {
  ADMISSIONS: YSTNJ_REVIEW_GROUPS[0],
  FINANCIAL_AID: YSTNJ_REVIEW_GROUPS[1],
} as const;

export const YSTNJ_REVIEW_MODULE_ID = {
  ADMISSIONS: YSTNJ_REVIEW_MODULE_IDS[0],
  FINANCIAL_AID: YSTNJ_REVIEW_MODULE_IDS[1],
} as const;

export const YSTNJ_RUNTIME_PERSONA = {
  PARENT: YSTNJ_RUNTIME_PERSONA_PROJECTIONS[0],
  STAFF: YSTNJ_RUNTIME_PERSONA_PROJECTIONS[1],
  ADMINISTRATOR: YSTNJ_RUNTIME_PERSONA_PROJECTIONS[2],
  SUPER_ADMINISTRATOR: YSTNJ_RUNTIME_PERSONA_PROJECTIONS[3],
  VIEWER: YSTNJ_RUNTIME_PERSONA_PROJECTIONS[4],
  GUEST: YSTNJ_RUNTIME_PERSONA_PROJECTIONS[5],
} as const;

export const YstnjCognitoGroupSchema = z.enum(YSTNJ_COGNITO_GROUPS);
export const YstnjReviewGroupSchema = z.enum(YSTNJ_REVIEW_GROUPS);
export const YstnjReviewModuleIdSchema = z.enum(YSTNJ_REVIEW_MODULE_IDS);
export const YstnjRuntimePersonaProjectionSchema = z.enum(
  YSTNJ_RUNTIME_PERSONA_PROJECTIONS,
);

export type YstnjCognitoGroup = z.infer<typeof YstnjCognitoGroupSchema>;
export type YstnjReviewGroup = z.infer<typeof YstnjReviewGroupSchema>;
export type YstnjReviewModuleId = z.infer<typeof YstnjReviewModuleIdSchema>;
export type YstnjRuntimePersonaProjection = z.infer<
  typeof YstnjRuntimePersonaProjectionSchema
>;

export const normalizeYstnjVocabularyToken = (value: unknown): string =>
  String(value ?? '')
    .trim()
    .toLowerCase();

const parseSchemaValue = <T>(
  schema: z.ZodType<T>,
  value: unknown,
): T | null => {
  const result = schema.safeParse(normalizeYstnjVocabularyToken(value));
  return result.success ? result.data : null;
};

export const parseCanonicalCognitoGroup = (
  value: unknown,
): YstnjCognitoGroup | null => parseSchemaValue(YstnjCognitoGroupSchema, value);

export const parseCanonicalReviewGroup = (
  value: unknown,
): YstnjReviewGroup | null => parseSchemaValue(YstnjReviewGroupSchema, value);

export const parseCanonicalReviewModuleId = (
  value: unknown,
): YstnjReviewModuleId | null =>
  parseSchemaValue(YstnjReviewModuleIdSchema, value);

export const parseRuntimePersonaProjection = (
  value: unknown,
): YstnjRuntimePersonaProjection | null =>
  parseSchemaValue(YstnjRuntimePersonaProjectionSchema, value);

export const normalizeCanonicalCognitoGroups = (
  groups: readonly unknown[] | null | undefined,
): YstnjCognitoGroup[] => [
  ...new Set((groups ?? []).map((group) => parseCanonicalCognitoGroup(group)).filter(Boolean)),
] as YstnjCognitoGroup[];

export const normalizeCanonicalReviewGroups = (
  groups: readonly unknown[] | null | undefined,
): YstnjReviewGroup[] => [
  ...new Set((groups ?? []).map((group) => parseCanonicalReviewGroup(group)).filter(Boolean)),
] as YstnjReviewGroup[];

export const YSTNJ_REVIEW_GROUP_BY_MODULE: Record<
  YstnjReviewModuleId,
  YstnjReviewGroup
> = {
  [YSTNJ_REVIEW_MODULE_ID.ADMISSIONS]: YSTNJ_REVIEW_GROUP.ADMISSIONS,
  [YSTNJ_REVIEW_MODULE_ID.FINANCIAL_AID]: YSTNJ_REVIEW_GROUP.FINANCIAL_AID,
};

export const YSTNJ_REVIEW_ROUTE_ROOT_BY_MODULE: Record<
  YstnjReviewModuleId,
  string
> = {
  [YSTNJ_REVIEW_MODULE_ID.ADMISSIONS]: '/school/admissions',
  [YSTNJ_REVIEW_MODULE_ID.FINANCIAL_AID]: '/school/financialaid',
};

export const YSTNJ_REVIEW_BYPASS_COGNITO_GROUPS = [
  YSTNJ_COGNITO_GROUP.ADMIN,
  YSTNJ_COGNITO_GROUP.OWNER,
  YSTNJ_COGNITO_GROUP.SUPER_ADMINISTRATOR,
] as const satisfies readonly YstnjCognitoGroup[];

export const YSTNJ_PARENT_COGNITO_GROUPS = [
  YSTNJ_COGNITO_GROUP.PARENT,
] as const satisfies readonly YstnjCognitoGroup[];

export const YSTNJ_RUNTIME_PERSONA_PROJECTION_BY_GROUP: Record<
  YstnjCognitoGroup,
  YstnjRuntimePersonaProjection
> = {
  [YSTNJ_COGNITO_GROUP.STAFF]: YSTNJ_RUNTIME_PERSONA.STAFF,
  [YSTNJ_COGNITO_GROUP.PARENT]: YSTNJ_RUNTIME_PERSONA.PARENT,
  [YSTNJ_COGNITO_GROUP.NURSE]: YSTNJ_RUNTIME_PERSONA.STAFF,
  [YSTNJ_COGNITO_GROUP.OWNER]: YSTNJ_RUNTIME_PERSONA.ADMINISTRATOR,
  [YSTNJ_COGNITO_GROUP.ADMIN]: YSTNJ_RUNTIME_PERSONA.ADMINISTRATOR,
  [YSTNJ_COGNITO_GROUP.VIEWER]: YSTNJ_RUNTIME_PERSONA.VIEWER,
  [YSTNJ_COGNITO_GROUP.EDITOR]: YSTNJ_RUNTIME_PERSONA.STAFF,
  [YSTNJ_COGNITO_GROUP.SUPER_ADMINISTRATOR]: YSTNJ_RUNTIME_PERSONA.SUPER_ADMINISTRATOR,
  [YSTNJ_COGNITO_GROUP.ADMISSIONS_REVIEW]: YSTNJ_RUNTIME_PERSONA.GUEST,
  [YSTNJ_COGNITO_GROUP.FINANCIAL_AID_REVIEW]: YSTNJ_RUNTIME_PERSONA.GUEST,
};

export const resolveRuntimePersonaProjectionForGroup = (
  group: YstnjCognitoGroup,
): YstnjRuntimePersonaProjection =>
  YSTNJ_RUNTIME_PERSONA_PROJECTION_BY_GROUP[group];
